// FUN boot self-check (SC-57555). The frontend embeds the PINNED contract hash it was built against. On boot it
// fetches a SAME-ORIGIN contract-hash manifest — served by the host that serves the bundle, declaring the
// contract the deployment/server is actually running — and asserts it EQUALS the pinned hash via the D11
// version gate (isSameContract, from the published @blocklabsdev/rgs-contracts package).
//
// FAIL CLOSED: a fetch error, a missing/empty served hash, or a mismatch => refuse. We must NEVER render a
// provably-fair proof recomputed with an engine the server did not run: a wrong "proof" is worse than none.
//
// THE TRANSITION (SC-65843, expand/contract): `predecessors` are earlier identities of the SAME game contract with the
// same math (pinned-hash.json "transition": a PF-text-only edit moved the hash). A served hash equal to one of them is
// accepted too — exactly those, and only while they are listed. Anything else is a mismatch.
import { isSameContract } from "@blocklabsdev/rgs-contracts";

// Pure, synchronous decision (unit-testable). Does the served hash match the pinned hash (or a listed predecessor)?
export function evaluateSelfCheck(pinnedHash, servedHash, predecessors = []) {
  if (isSameContract(servedHash, pinnedHash)) {
    return { ok: true, pinnedHash, servedHash, matched: "pinned" };
  }
  if (predecessors.some((p) => isSameContract(servedHash, p))) {
    return { ok: true, pinnedHash, servedHash, matched: "predecessor" };
  }
  return {
    ok: false,
    pinnedHash,
    servedHash: servedHash ?? null,
    reason: servedHash
      ? "served contract hash != pinned contract hash"
      : "no contract hash served (fail closed)",
  };
}

// Async wrapper with an INJECTABLE manifest fetcher (the browser passes a real same-origin fetch; tests pass a
// fake). Any thrown/rejected fetch is caught and turned into a fail-closed result — this never throws.
export async function selfCheckContract(pinnedHash, fetchManifest, game = "dice", predecessors = []) {
  let manifest;
  try {
    manifest = await fetchManifest();
  } catch (e) {
    const msg = e && e.message ? e.message : String(e);
    return { ok: false, pinnedHash, servedHash: null, reason: `manifest fetch failed: ${msg} (fail closed)` };
  }
  return evaluateSelfCheck(pinnedHash, manifest ? manifest[game] : undefined, predecessors);
}
